import { Menu, MenuButton, Box, MenuList } from '@chakra-ui/react';
import React, { ReactNode, useEffect, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';
import { useParentBasketContext } from '../../../context/ParentBasketProvider';
import { useParentDashboardContextContext } from '../../../context/ParentDashboardContext';
import useAuth from '../../../hooks/useAuth';
import { IconName } from '../../../shared';
import CustomChakraButton from '../../CustomChakraButton/CustomChakraButton';
import IconComponent from '../../IconComponent/IconComponent';
import Basket from '../../Basket/Basket';
import { MobileLanguageSelector } from '../../LanguagesSelectors/MobileParentLangugage';
import { AVAILABLE_LANGUAGE_OPTIONS } from '../../LanguagesSelectors/helpers';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';

import './ParentHeader.css';
import { useUnreadThreadCountContext } from '../../../context/UnreadMessageContext';

interface Props {
  dropdownMenuList: ReactNode;
  notificationList: ReactNode | null;
}

const ParentHeader = ({ dropdownMenuList }: Props) => {
  const location = useLocation();
  const { basketStatus, setBasketStatus } = useParentDashboardContextContext();
  const { parentBasketContext } = useParentBasketContext();
  const { auth, logout } = useAuth();
  const { parentDashboardContext } = useParentDashboardContextContext();
  const { unreadThreadCount } = useUnreadThreadCountContext();
  const { t, i18n } = useSafeTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [isSubMenu, setIsSubMenu] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<string | null>(null);
  const languageMenuItemElement = AVAILABLE_LANGUAGE_OPTIONS.find(
    (option) => option.code === i18n.language
  );

  const mobileMenuLinks: {
    icon: IconName;
    title: string | React.ReactNode;
    link: string;
  }[] = [
    {
      icon: 'happyHomeIcon',
      title: t('parent_header.home'),
      link: '/parent/main'
    },
    {
      icon: 'calendar',
      title: t('parent_header.orders_absences'),
      link: '/parent/absences'
    },
    {
      icon: 'cutlery',
      title: t('parent_header.menu'),
      link: '/parent/meal-plan'
    },
    {
      icon: 'users',
      title: t('parent_header.consumers'),
      link: '/parent/parent-children/assigned-accounts'
    },
    {
      icon: 'history',
      title: t('parent_header.order_history'),
      link: '/parent/order-history'
    },
    {
      icon: 'message',
      title: (
        <>
          {t('child_owner_dashboard_element.messages')}{' '}
          {unreadThreadCount > 0 && (
            <span className="ml-2 mb-1.5 text-xs bg-brand-600 text-white rounded-full px-1.5 py-0.5">
              {unreadThreadCount}
            </span>
          )}
        </>
      ),
      link: '/parent/mail-box/messages'
    }
  ];

  const handleLanguageMenuClick = () => {
    setIsSubMenu(true);
    setSelectedMenu('language');
  };

  const handleBackClick = () => {
    setIsSubMenu(false);
    setSelectedMenu(null);
  };

  useEffect(() => {
    if (!showMenu) {
      setIsSubMenu(false);
      setSelectedMenu(null);
    }

    return () => {
      setIsSubMenu(false);
      setSelectedMenu(null);
    };
  }, [showMenu]);

  useEffect(() => {
    setShowMenu(false);
  }, [location.pathname]);

  return (
    <header className="w-full flex flex-row items-center justify-between lg:justify-end px-4 lg:px-8 py-4 lg:py-2.5 bg-white relative z-40 shadow-sm">
      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 flex flex-col bg-white z-50 transform transition-transform duration-300 ease-in-out ${showMenu ? 'translate-x-0' : 'translate-x-full'} lg:hidden`}>
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
          {isSubMenu && (
            <button onClick={handleBackClick} className="flex items-center">
              <IconComponent iconName="chevronLeft" color="#667085" />
            </button>
          )}
          <Link to="/" onClick={() => setShowMenu(false)}>
            <IconComponent className="w-3/4 h-10" iconName="logo" />
          </Link>
          <button onClick={() => setShowMenu(false)} className="flex items-center">
            <IconComponent iconName="xClose" className="w-5 h-5" />
          </button>
        </div>

        {/* Submenu */}
        {isSubMenu && selectedMenu === 'language' ? (
          <div className="p-6">
            <p className="mb-6 text-gray-700">{t('parent_header.change_language')}</p>
            <MobileLanguageSelector closeMenu={() => setShowMenu(false)} />
          </div>
        ) : (
          <div className="flex flex-col justify-between overflow-y-auto p-6 pb-spacing-11xl">
            <div className="flex flex-col gap-6">
              {mobileMenuLinks.map((link) => (
                <Link
                  key={String(link.title)}
                  to={link.link}
                  onClick={() => setShowMenu(false)}
                  className="flex items-center gap-4 text-gray-700 font-semibold">
                  <IconComponent iconName={link.icon} color="#667085" />
                  <span>{link.title}</span>
                </Link>
              ))}
              <button
                onClick={handleLanguageMenuClick}
                className="flex items-center gap-4 text-gray-700 font-semibold">
                {languageMenuItemElement?.flag || <IconComponent iconName="languagePl" />}
                <span>{languageMenuItemElement?.label || t('parent_header.choose_language')}</span>
                <IconComponent iconName="chevronRight" color="#667085" />
              </button>
            </div>

            <div className="mt-8 flex flex-col gap-6">
              <Link
                to="/parent/contact"
                onClick={() => setShowMenu(false)}
                className="flex items-center gap-4">
                <IconComponent iconName="phone" color="#667085" />
                <span>{t('parent_header.contact')}</span>
              </Link>
              <Link
                to="/parent/settings"
                onClick={() => setShowMenu(false)}
                className="flex items-center gap-4">
                <IconComponent iconName="settings" color="#667085" />
                <span>{t('parent_header.settings')}</span>
              </Link>
              <button
                onClick={logout}
                className="flex justify-between items-center w-full mt-4 text-left">
                <div className="flex flex-col text-sm">
                  <span className="text-gray-700 font-medium">
                    {auth.loggedUserDto.firstName} {auth.loggedUserDto.lastName}
                  </span>
                  <span className="text-gray-500">{auth.loggedUserDto.login}</span>
                </div>
                <IconComponent iconName="logOutDoor" color="#475467" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Left Section */}
      <div className="flex lg:hidden items-center">
        <Link to="/">
          <IconComponent className="w-3/4 h-10" iconName="logo" />
        </Link>
      </div>

      {/* Center Section (Pay Balance Button) */}
      {parentDashboardContext?.showPaySaldoButton && (
        <div className="hidden lg:flex flex-1 justify-center md:justify-start">
          <Link to={location.pathname.replace('main', 'payoff-balance')}>
            <CustomChakraButton
              hierarchy="secondaryColor"
              size="lg"
              iconPosition="left"
              icon="wallet">
              {t('parent_header.pay_balance')}
            </CustomChakraButton>
          </Link>
        </div>
      )}

      {/* Right Section */}
      <div className="flex flex-row-reverse md:flex-row items-center gap-4">
        {/* Mobile Menu Button */}
        <button onClick={() => setShowMenu(true)} className="lg:hidden p-2 focus:outline-none">
          <IconComponent iconName="menu" className="w-6 h-6" />
        </button>

        {/* Basket Button */}
        <button
          className={`relative flex items-center gap-2 p-2 rounded-md border ${basketStatus.show ? 'bg-brand-100 shadow-md' : 'border-transparent'}`}
          disabled={basketStatus.forceShow}
          onClick={() => setBasketStatus((prev) => ({ ...prev, show: !prev.show }))}>
          <IconComponent iconName="basket" color="#344054" />
          <span className="absolute top-[-8px] right-[-8px] text-xs bg-brand-600 text-white rounded-full px-1.5 py-0.5">
            {parentBasketContext.ordersForChildren.flatMap((c) => c.orderedItems).length}
          </span>
        </button>

        {/* User Menu */}
        <Menu>
          <MenuButton
            as={Box}
            aria-label="Options"
            className="hidden lg:flex items-center justify-center w-9 h-9 text-center cursor-pointer rounded-full bg-grayLight-100 border border-grayLight-300">
            <p className="text-gray-700 text-sm font-bold uppercase">
              {auth.loggedUserDto.firstName[0]}
              {auth.loggedUserDto.lastName[0]}
            </p>
          </MenuButton>
          <MenuList zIndex={999}>{dropdownMenuList}</MenuList>
        </Menu>
      </div>

      {/* Koszyk po prawej (desktop) */}
      <div
        className={`fixed right-0 top-0 h-full w-[400px] bg-white border-l z-40 transform transition-transform duration-300 ease-in-out ${basketStatus.show ? 'translate-x-0' : 'translate-x-full'}`}>
        <Basket />
      </div>
    </header>
  );
};

export default ParentHeader;
